import React from 'react';
import { 
  DollarSign,
  TrendingUp,
  CheckCircle,
  BarChart3,
  Users,
  Download,
  FileText,
  CreditCard
} from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const AdminReports = ({ dashboardData, agents, transactions, filters, setFilters, exportData }) => {
  // Filter transactions based on current filters
  const filteredTransactions = transactions.filter(transaction => {
    // Date filter
    if (filters.dateRange !== 'all') {
      const transactionDate = new Date(transaction.createdAt);
      const daysAgo = parseInt(filters.dateRange);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
      if (transactionDate < cutoffDate) return false;
    }
    
    // Agent filter
    if (filters.agent !== 'all' && transaction.agentId !== filters.agent) {
      return false;
    }
    
    // Status filter
    if (filters.status !== 'all' && transaction.status !== filters.status) {
      return false;
    }
    
    // Payment method filter
    if (filters.paymentMethod !== 'all' && transaction.paymentMethod !== filters.paymentMethod) {
      return false;
    }
    
    return true;
  });
  
  // Calculate filtered analytics
  const filteredDashboardData = {
    totalRevenue: filteredTransactions
      .filter(t => t.status === 'completed')
      .reduce((sum, t) => sum + (t.amount || 0), 0),
    totalTransactions: filteredTransactions.length,
    successRate: filteredTransactions.length > 0 
      ? (filteredTransactions.filter(t => t.status === 'completed').length / filteredTransactions.length) * 100 
      : 0,
    avgTransaction: filteredTransactions.length > 0 
      ? filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0) / filteredTransactions.length 
      : 0
  };
  
  // Filter agents to show only those with transactions in filtered data
  const filteredAgents = agents.filter(agent => 
    filteredTransactions.some(t => t.agentId === agent.agentId)
  ).map(agent => {
    const agentTransactions = filteredTransactions.filter(t => t.agentId === agent.agentId);
    const completedTransactions = agentTransactions.filter(t => t.status === 'completed');
    return {
      ...agent,
      transactionCount: agentTransactions.length,
      revenue: completedTransactions.reduce((sum, t) => sum + (t.amount || 0), 0),
      successRate: agentTransactions.length > 0 
        ? (completedTransactions.length / agentTransactions.length) * 100 
        : 0
    };
  }).sort((a, b) => b.revenue - a.revenue);
  return (
    <div className="space-y-8">
      {/* Executive Summary Cards with PayLow Branding */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Total Revenue</p>
              <p className="text-2xl font-bold">
                {formatCurrency(filteredDashboardData.totalRevenue)}
              </p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <DollarSign size={24} />
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <TrendingUp size={16} className="mr-1" />
            <span className="text-sm text-red-100">+12.5% from last month</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Transactions</p>
              <p className="text-2xl font-bold">{filteredDashboardData.totalTransactions}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <CreditCard size={24} />
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <TrendingUp size={16} className="mr-1" />
            <span className="text-sm text-blue-100">+8.2% from last month</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Success Rate</p>
              <p className="text-2xl font-bold">
                {filteredDashboardData.successRate.toFixed(1)}%
              </p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <CheckCircle size={24} />
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <TrendingUp size={16} className="mr-1" />
            <span className="text-sm text-green-100">+2.1% improvement</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Avg. Transaction</p>
              <p className="text-2xl font-bold">
                {formatCurrency(filteredDashboardData.avgTransaction)}
              </p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <BarChart3 size={24} />
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <TrendingUp size={16} className="mr-1" />
            <span className="text-sm text-orange-100">+5.7% increase</span>
          </div>
        </div>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Payment Methods Analysis */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <BarChart3 size={20} className="mr-2 text-blue-600" />
              Payment Methods Breakdown
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-3"></div>
                  <span className="font-medium">Credit/Debit Cards</span>
                </div>
                <div className="text-right">
                  <p className="font-bold">78.5%</p>
                  <p className="text-sm text-gray-500">{formatCurrency(filteredDashboardData.totalRevenue * 0.785)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-500 rounded mr-3"></div>
                  <span className="font-medium">Cash Payments</span>
                </div>
                <div className="text-right">
                  <p className="font-bold">18.2%</p>
                  <p className="text-sm text-gray-500">{formatCurrency(filteredDashboardData.totalRevenue * 0.182)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-purple-500 rounded mr-3"></div>
                  <span className="font-medium">Company Credit</span>
                </div>
                <div className="text-right">
                  <p className="font-bold">3.3%</p>
                  <p className="text-sm text-gray-500">{formatCurrency(filteredDashboardData.totalRevenue * 0.033)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Performing Agents */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Users size={20} className="mr-2 text-green-600" />
              Top Performing Agents
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {filteredAgents.length > 0 ? filteredAgents.slice(0, 5).map((agent, index) => (
                <div key={agent.agentId} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white mr-3 ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{agent.firstName} {agent.lastName}</p>
                      <p className="text-sm text-gray-500">ID: {agent.agentId}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">
                      {formatCurrency(agent.revenue)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {agent.transactionCount} sales • {agent.successRate.toFixed(1)}% success
                    </p>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 text-gray-500">
                  <Users size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No agents found</p>
                  <p className="text-sm">Try adjusting your filters to see agent performance data.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Comprehensive Data Export with PayLow Branding */}
      <div className="bg-white rounded-xl shadow-sm border-2 border-transparent bg-gradient-to-r from-red-500 to-blue-500 bg-clip-border">
        <div className="bg-white m-0.5 rounded-xl">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Download size={20} className="mr-2 text-purple-600" />
              PayLow Insurance - Export & Reporting Tools
            </h3>
            <p className="text-sm text-gray-500 mt-1">Generate comprehensive business intelligence reports</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <button
                onClick={() => exportData('transactions')}
                className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-lg hover:from-blue-100 hover:to-blue-200 transition-all duration-200 border border-blue-200"
              >
                <FileText size={20} className="mr-2" />
                Export All Transactions
              </button>
              <button
                onClick={() => exportData('agents')}
                className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-green-50 to-green-100 text-green-700 rounded-lg hover:from-green-100 hover:to-green-200 transition-all duration-200 border border-green-200"
              >
                <Users size={20} className="mr-2" />
                Export Agent Performance
              </button>
              <button
                onClick={() => exportData('revenue')}
                className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 rounded-lg hover:from-purple-100 hover:to-purple-200 transition-all duration-200 border border-purple-200"
              >
                <BarChart3 size={20} className="mr-2" />
                Export Revenue Report
              </button>
            </div>

            {/* Advanced Filters */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Advanced Filtering Options</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                  <select className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                    <option>Last 30 Days</option>
                    <option>Last 90 Days</option>
                    <option>Last 6 Months</option>
                    <option>Last Year</option>
                    <option>Custom Range</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agent</label>
                  <select className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                    <option>All Agents</option>
                    {agents.map(agent => (
                      <option key={agent.agentId} value={agent.agentId}>
                        {agent.firstName} {agent.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                    <option>All Methods</option>
                    <option>Credit/Debit Card</option>
                    <option>Cash</option>
                    <option>Company Credit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                    <option>All Statuses</option>
                    <option>Completed</option>
                    <option>Pending</option>
                    <option>Failed</option>
                    <option>Refunded</option>
                  </select>
                </div>
              </div>
            </div>

            {/* PayLow Insurance Branding Footer */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                <span className="font-bold text-red-500">Pay</span><span className="font-bold text-blue-500">Low</span> Insurance • Advanced Business Intelligence Suite
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;